import { useEffect, useState } from 'react';
import type { BVH } from 'three/examples/jsm/loaders/BVHLoader.js';

import { loadBVHFiles, isMorphTrack, resolvePrimaryPositionTrackName } from '@cartwhl/3d/bvh';
import type { RotationOffsets } from '@cartwhl/common/types';

export interface UseBVHAnimationConfig {
  bvh?: BVH;
  bvhUrl?: string;
  rotationOffsets?: RotationOffsets;
}

export interface UseBVHAnimationsConfig {
  bvhUrls: string[];
  rotationOffsets?: RotationOffsets;
}

export function normalizeBVHForPlayback(bvh: BVH): BVH {
  const rootPositionTrackName = resolvePrimaryPositionTrackName(bvh);
  const userData = (bvh.clip as unknown as { userData?: Record<string, unknown> })
    .userData;
  const morphJoints = userData?.morphJoints as Set<string> | undefined;
  const normalizedClip = bvh.clip.clone();
  normalizedClip.tracks = normalizedClip.tracks.filter((track) => {
    if (morphJoints && isMorphTrack(track.name, morphJoints)) return false;
    return (
      track.name.includes('quaternion') ||
      (rootPositionTrackName != null && track.name === rootPositionTrackName)
    );
  });

  (normalizedClip as unknown as { userData?: Record<string, unknown> }).userData =
    userData;

  return {
    skeleton: bvh.skeleton.clone(),
    clip: normalizedClip,
  };
}

export async function loadBVHAnimation(
  bvhUrl: string,
  rotationOffsets?: RotationOffsets,
) {
  const [resolved] = await loadBVHFiles([bvhUrl], rotationOffsets);
  return resolved ? normalizeBVHForPlayback(resolved) : null;
}

export async function loadBVHAnimations(
  bvhUrls: string[],
  rotationOffsets?: RotationOffsets,
) {
  const resolved = await loadBVHFiles(bvhUrls, rotationOffsets);
  return resolved.map(normalizeBVHForPlayback);
}

export function useBVHAnimations({
  bvhUrls,
  rotationOffsets,
}: UseBVHAnimationsConfig) {
  const [loadedBvhs, setLoadedBvhs] = useState<BVH[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let isMounted = true;

    if (bvhUrls.length === 0) {
      setLoadedBvhs([]);
      setError(null);
      setIsLoading(false);
      return () => {
        isMounted = false;
      };
    }

    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const resolved = await loadBVHAnimations(bvhUrls, rotationOffsets);
        if (!isMounted) return;
        setLoadedBvhs(resolved);
      } catch (loadError) {
        if (!isMounted) return;
        setLoadedBvhs([]);
        setError(
          loadError instanceof Error
            ? loadError
            : new Error('Failed to load BVH animation data.'),
        );
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, [bvhUrls, rotationOffsets]);

  return { bvhs: loadedBvhs, isLoading, error };
}

export function useBVHAnimation({
  bvh,
  bvhUrl,
  rotationOffsets,
}: UseBVHAnimationConfig) {
  const [loadedBvh, setLoadedBvh] = useState<BVH | null>(() =>
    bvh ? normalizeBVHForPlayback(bvh) : null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let isMounted = true;

    if (bvh) {
      setLoadedBvh(normalizeBVHForPlayback(bvh));
      setError(null);
      setIsLoading(false);
      return () => {
        isMounted = false;
      };
    }

    if (!bvhUrl) {
      setLoadedBvh(null);
      setError(null);
      setIsLoading(false);
      return () => {
        isMounted = false;
      };
    }

    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const resolved = await loadBVHAnimation(bvhUrl, rotationOffsets);
        if (!isMounted) return;
        setLoadedBvh(resolved ?? null);
      } catch (loadError) {
        if (!isMounted) return;
        setLoadedBvh(null);
        setError(
          loadError instanceof Error
            ? loadError
            : new Error('Failed to load BVH animation data.'),
        );
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, [bvh, bvhUrl, rotationOffsets]);

  return { bvh: loadedBvh, isLoading, error };
}
